import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CalendarPlus, Check, Plane, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { selfServiceHomePath, useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import {
  applyForLeave,
  cancelLeaveRequest,
  decideLeaveRequest,
  getMyLeaveBalances,
  getMyLeaveRequests,
  getPendingLeave
} from '../../services/api/implementation/impl';
import {
  Callout,
  EmptyState,
  PageBody,
  StatusPill,
  Tile
} from '../../../shared/components/ui/primitives';

/* Same chip as ManagementHeaderTabs. */
const tabClass = (active) =>
  `rounded-md px-4 py-1.5 text-sm font-medium border transition-all duration-200 shrink-0 ${
    active
      ? 'bg-[#2A565E] text-white border-[#2A565E]'
      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
  }`;

/**
 * My leave, on the web (30 Sep).
 *
 * Applying for leave was app-only, and approving it was only on the HR Leave
 * page — so a manager whose reports sent requests had no way to decide them on
 * the web. This is the app's leave screen for everybody: balances, apply,
 * one's own requests (with cancel), and "Requests to approve" whenever
 * somebody is waiting on this person. Same endpoints and rules as the app.
 */
const MyLeave = () => {
  const { onBack } = useSmartBack({ fallback: selfServiceHomePath() });
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [applying, setApplying] = useState(false);

  const balances = useQuery({ queryKey: ['myLeaveBalances'], queryFn: getMyLeaveBalances });
  const requests = useQuery({ queryKey: ['myLeaveRequests'], queryFn: getMyLeaveRequests });
  const pending = useQuery({ queryKey: ['pendingLeave'], queryFn: getPendingLeave, retry: false });

  const toApprove = pending.data?.data ?? [];
  const tabs = [
    { key: 'mine', label: 'My leave' },
    ...(toApprove.length
      ? [{ key: 'approve', label: `Requests to approve (${String(toApprove.length).padStart(2, '0')})` }]
      : [])
  ];
  const active = tabs.find((t) => t.key === searchParams.get('tab')) ?? tabs[0];

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['myLeaveBalances'] });
    queryClient.invalidateQueries({ queryKey: ['myLeaveRequests'] });
    queryClient.invalidateQueries({ queryKey: ['pendingLeave'] });
  };

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'My Leave' }}>
        {tabs.length > 1 && (
          <div className="flex items-center gap-2" role="tablist" aria-label="Leave">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={t.key === active.key}
                onClick={() => setSearchParams(t.key === 'mine' ? {} : { tab: t.key })}
                className={tabClass(t.key === active.key)}>
                {t.label}
              </button>
            ))}
          </div>
        )}
      </Header>
      <PageBody>
        {active.key === 'approve' ? (
          <ApproveTab rows={toApprove} onDone={refresh} />
        ) : (
          <MineTab
            balances={balances}
            requests={requests}
            onApply={() => setApplying(true)}
            onDone={refresh}
          />
        )}
      </PageBody>
      {applying && (
        <ApplyModal
          balances={balances.data?.balances ?? []}
          onClose={() => setApplying(false)}
          onDone={() => {
            setApplying(false);
            refresh();
          }}
        />
      )}
    </Layout>
  );
};

// --- my leave -------------------------------------------------------------------

const MineTab = ({ balances, requests, onApply, onDone }) => {
  const cancel = useMutation({
    mutationFn: (id) => cancelLeaveRequest(id),
    onSuccess: () => {
      toast.success('Leave withdrawn');
      onDone();
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not withdraw the leave')
  });

  const list = balances.data?.balances ?? [];
  const rows = requests.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-800">
          Balances {balances.data?.year ? `· ${balances.data.year}` : ''}
        </h2>
        <button
          type="button"
          onClick={onApply}
          disabled={!list.length}
          className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
          <CalendarPlus size={15} /> Apply for leave
        </button>
      </div>

      {balances.isLoading ? (
        <Loader />
      ) : !list.length ? (
        <EmptyState
          title="No leave types apply to you yet"
          hint="HR sets up leave types under Workforce → Employees → Leave."
        />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {list.map((b) => (
            <Tile
              key={b.leave_type_id}
              variant="card"
              label={b.name}
              value={formatDays(b.balance)}
              hint={`${formatDays(b.used)} used${b.is_paid === false ? ' · unpaid' : ''}`}
              icon={<Plane size={18} />}
              tone={Number(b.balance) > 0 ? 'brand' : 'neutral'}
            />
          ))}
        </div>
      )}

      <h2 className="pt-2 text-sm font-semibold text-gray-800">My requests</h2>
      {requests.isLoading ? (
        <Loader />
      ) : !rows.length ? (
        <EmptyState title="You haven't applied for leave yet" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <Th>Type</Th>
                <Th>Dates</Th>
                <Th>Days</Th>
                <Th>Status</Th>
                <Th>Approver</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const cancellable =
                  r.status === 'PENDING' ||
                  (r.status === 'APPROVED' && String(r.from_date).slice(0, 10) >= todayKey());
                return (
                  <tr key={r.id} className="border-t border-gray-100">
                    <Td>{r.leaveType?.name ?? '—'}</Td>
                    <Td>
                      {formatDate(r.from_date)}
                      {String(r.from_date) !== String(r.to_date) && <> → {formatDate(r.to_date)}</>}
                    </Td>
                    <Td>{formatDays(r.days)}</Td>
                    <Td>
                      <StatusPill dense status={r.status} />
                      {r.decision_remarks && (
                        <div className="mt-1 text-xs text-gray-500">{r.decision_remarks}</div>
                      )}
                    </Td>
                    <Td className="text-xs text-gray-600">{r.approver?.name ?? 'Not assigned yet'}</Td>
                    <Td>
                      {cancellable && (
                        <button
                          type="button"
                          disabled={cancel.isPending}
                          onClick={() => cancel.mutate(r.id)}
                          className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 disabled:opacity-50">
                          Withdraw
                        </button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ApplyModal = ({ balances, onClose, onDone }) => {
  const [typeId, setTypeId] = useState(String(balances[0]?.leave_type_id ?? ''));
  const [from, setFrom] = useState(todayKey());
  const [to, setTo] = useState(todayKey());
  const [halfDay, setHalfDay] = useState(false);
  const [reason, setReason] = useState('');

  const singleDay = from === to;
  // An estimate, as on the app: holidays and week-offs inside the dates are
  // not charged, and the server's figure comes back on submit.
  const estimate = useMemo(() => {
    const days = Math.round((new Date(to) - new Date(from)) / 86400000) + 1;
    if (!(days > 0)) return 0;
    return halfDay && singleDay ? 0.5 : days;
  }, [from, to, halfDay, singleDay]);
  const chosen = balances.find((b) => String(b.leave_type_id) === typeId);

  const apply = useMutation({
    mutationFn: () =>
      applyForLeave({
        leave_type_id: Number(typeId),
        from_date: from,
        to_date: to,
        first_day_half: halfDay && singleDay ? 'SECOND_HALF' : null,
        reason: reason.trim() || null
      }),
    onSuccess: () => {
      toast.success('Leave requested');
      onDone();
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not apply for leave')
  });

  const problem = !typeId
    ? 'Choose a leave type'
    : to < from
      ? 'The end date cannot be before the start date'
      : from.slice(0, 4) !== to.slice(0, 4)
        ? 'Split requests that cross a year end'
        : null;

  return (
    <Modal name="Apply for leave" onClose={onClose} width="480px">
      <div className="space-y-3 text-sm">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600">Leave type</span>
          <select
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2">
            {balances.map((b) => (
              <option key={b.leave_type_id} value={b.leave_type_id}>
                {b.name} — {formatDays(b.balance)} left
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                if (e.target.value > to) setTo(e.target.value);
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">To</span>
            <input
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </label>
        </div>
        {singleDay && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={halfDay}
              onChange={(e) => setHalfDay(e.target.checked)}
              className="h-4 w-4 accent-brand-700"
            />
            Half day
          </label>
        )}
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600">Reason (optional)</span>
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
          />
        </label>
        <Callout tone="info">
          About {formatDays(estimate)} day{estimate === 1 ? '' : 's'}
          {chosen ? ` of ${formatDays(chosen.balance)} ${chosen.name} left` : ''}. An estimate —
          holidays and week-offs are not charged; the exact figure comes back when you submit.
        </Callout>
        {problem && <p className="text-xs text-danger-fg">{problem}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700">
            Cancel
          </button>
          <button
            type="button"
            disabled={Boolean(problem) || apply.isPending}
            onClick={() => apply.mutate()}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {apply.isPending ? 'Sending…' : 'Send request'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// --- requests to approve ----------------------------------------------------------

const ApproveTab = ({ rows, onDone }) => {
  const decide = useMutation({
    mutationFn: ({ id, approve }) => decideLeaveRequest(id, approve),
    onSuccess: (_, v) => {
      toast.success(v.approve ? 'Leave approved' : 'Leave rejected');
      onDone();
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not decide the request')
  });

  if (!rows.length) return <EmptyState title="Nothing waiting for you" />;

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <Th>Employee</Th>
            <Th>Type</Th>
            <Th>Dates</Th>
            <Th>Days</Th>
            <Th>Balance</Th>
            <Th>Reason</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            // Approving beyond the balance is refused server-side.
            const short = Number(row.balance_available) < Number(row.days);
            return (
              <tr key={row.id} className="border-t border-gray-100">
                <Td className="font-medium text-gray-900">{row.user?.name ?? `#${row.user_id}`}</Td>
                <Td>{row.leaveType?.name ?? '—'}</Td>
                <Td>
                  {formatDate(row.from_date)}
                  {String(row.from_date) !== String(row.to_date) && <> → {formatDate(row.to_date)}</>}
                </Td>
                <Td>{formatDays(row.days)}</Td>
                <Td className={short ? 'font-medium text-danger-fg' : ''}>
                  {formatDays(row.balance_available)}
                  {short && <div className="text-xs">not enough</div>}
                </Td>
                <Td className="max-w-xs text-xs text-gray-600">{row.reason ?? '—'}</Td>
                <Td>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={decide.isPending || short}
                      title={short ? 'Not enough balance — this would be refused' : undefined}
                      onClick={() => decide.mutate({ id: row.id, approve: true })}
                      className="flex items-center gap-1 rounded bg-success px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
                      <Check size={13} /> Approve
                    </button>
                    <button
                      type="button"
                      disabled={decide.isPending}
                      onClick={() => decide.mutate({ id: row.id, approve: false })}
                      className="flex items-center gap-1 rounded border border-danger-border px-3 py-1.5 text-xs font-medium text-danger-fg disabled:opacity-50">
                      <X size={13} /> Reject
                    </button>
                  </div>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// --- bits ----------------------------------------------------------------------------

const Th = ({ children }) => (
  <th className="whitespace-nowrap px-4 py-2 text-left text-xs font-medium">{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-2 align-top ${className}`}>{children}</td>
);

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

function formatDate(value) {
  if (!value) return '—';
  const key = String(value).slice(0, 10);
  const [y, m, d] = key.split('-').map(Number);
  if (!y) return key;
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function formatDays(value) {
  const n = Number(value || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '');
}

export default MyLeave;
